import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import type { NodeFormProps, ScanNodeType } from '@/nodes';
import { equipmentTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { ScanDataSchema, type ScanDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { AppEquipmentTypeSelectField } from './FormUI';

type FormInput = z.input<typeof ScanDataSchema>;

const ScanForm: FC<NodeFormProps<ScanNodeType>> = ({ spaceId, flowId, node, updateNode, cancelUpdate }) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, ScanDataType>({
        resolver: zodResolver(ScanDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: ScanDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <div className="flex flex-col gap-2">
                        <AppEquipmentTypeSelectField
                            control={form.control}
                            name="equipmentTypeId"
                            label="Equipment Type"
                            placeholder="Select Equipment Type"
                            description="Select the Equipment Type that the user will scan for"
                            equipmentTypes={equipmentTypes}
                            allowContextSelection
                        />
                    </div>
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default ScanForm;
